import React from "react";
import type { JSONContent } from "@tiptap/core";
import {
  scrollToHeading,
  scrollToHeadingByTitle,
  slugifyHeading,
  isTOCEntry,
  extractTOCTitle,
  normalizeTitle,
} from "./headingUtils";
import { List, ListItem, ListItemText } from "@material-ui/core";

export interface TOCItem {
  id: string;
  text: string;
  title: string;
  level: number;
  itemIndex: number;
  headingId: string | null; // DOM id of the heading this entry points to
}

const getTOCLevel = (text: string): number => {
  const match = text.match(/^\s*(\d+(?:\.\d+)*)/);
  return match ? match[1].split(".").length : 1;
};

const nodeText = (n: JSONContent): string =>
  n.text ?? (n.content ?? []).map(nodeText).join("");

/** Pass the FULL document JSON (all pages), not a single editor. */
export const extractTOCItems = (
  doc: JSONContent | null | undefined,
): TOCItem[] => {
  if (!doc) return [];

  const headings: { id: string; key: string }[] = [];
  const entries: string[] = [];

  const walk = (node: JSONContent) => {
    if (node.type === "heading") {
      const text = nodeText(node).trim();
      if (text) {
        headings.push({
          id: node.attrs?.id ?? `heading-${slugifyHeading(text)}`,
          key: normalizeTitle(text),
        });
      }
      return;
    }
    if (node.type === "paragraph") {
      const text = nodeText(node).trim();
      if (text && isTOCEntry(text)) entries.push(text);
      return;
    }
    node.content?.forEach(walk);
  };
  walk(doc);

  // The Nth TOC entry with a given title maps to the Nth heading with that title.
  const seen = new Map<string, number>();

  return entries.map((text, index) => {
    const title = extractTOCTitle(text);
    const key = normalizeTitle(text);
    const occurrence = seen.get(key) ?? 0;
    seen.set(key, occurrence + 1);

    const candidates = headings.filter((h) => h.key === key);
    const target = candidates[occurrence] ?? candidates[0] ?? null;

    return {
      id: `toc-${index}-${slugifyHeading(title)}`,
      text,
      title,
      level: getTOCLevel(text),
      itemIndex: index,
      headingId: target?.id ?? null,
    };
  });
};

export const TableOfContents: React.FC<{
  items: TOCItem[];
  onNavigate?: () => void;
  title?: string | null; // pass null when the parent already shows a heading
}> = ({ items, onNavigate, title = "Table of Contents" }) => {
  if (items.length === 0) return null;

  const handleClick = (item: TOCItem) => {
    const key = normalizeTitle(item.title);
    const occurrence = items
      .slice(0, item.itemIndex)
      .filter((i) => normalizeTitle(i.title) === key).length;

    const ok =
      (item.headingId && scrollToHeading(item.headingId)) ||
      scrollToHeadingByTitle(item.title, occurrence);

    if (!ok) {
      console.warn(`Could not find heading for: "${item.title}"`);
      return;
    }
    onNavigate?.();
  };

  return (
    <nav className="docgen-toc" aria-label="Table of contents">
      {title && (
        <div style={{ fontWeight: 600, margin: "12px 0 4px", fontSize: 14 }}>
          {title}
        </div>
      )}

      <List dense disablePadding>
        {items.map((item) => {
          // "1.2 Intake information" instead of "1.2 Intake information ..... 4"
          const num = item.text.match(/^\s*(\d+(?:\.\d+)*\.?)/)?.[1];
          const label = num ? `${num} ${item.title}` : item.title;

          return (
            <ListItem
              key={`${item.id}-${item.itemIndex}`}
              button
              onClick={() => handleClick(item)}
              style={{ paddingLeft: 8 + (item.level - 1) * 16 }}
            >
              <ListItemText
                primary={label}
                primaryTypographyProps={{
                  style: {
                    fontSize: item.level === 1 ? 14 : 13,
                    fontWeight: item.level === 1 ? 600 : 400,
                  },
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </nav>
  );
};
